import { useEffect, useState } from "react";
import { RefreshCw } from "@/components/icons";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  APP_UPDATE_AVAILABLE_EVENT,
  clearCacheAndHardRefresh,
  getPendingAppUpdate,
  snoozeAppUpdate,
  type AppUpdateAvailableDetail,
} from "@/lib/app-update";

export function AppUpdateDialog() {
  const [availableUpdate, setAvailableUpdate] =
    useState<AppUpdateAvailableDetail | null>(() => getPendingAppUpdate());
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const syncPendingUpdate = () => {
      setAvailableUpdate(getPendingAppUpdate());
    };

    const handleAvailableUpdate = (event: Event) => {
      const detail = (event as CustomEvent<AppUpdateAvailableDetail>).detail;
      if (!detail?.buildId) return;
      setAvailableUpdate(detail);
    };

    window.addEventListener(APP_UPDATE_AVAILABLE_EVENT, handleAvailableUpdate);
    window.addEventListener("focus", syncPendingUpdate);
    window.addEventListener("pageshow", syncPendingUpdate);
    document.addEventListener("visibilitychange", syncPendingUpdate);
    syncPendingUpdate();

    return () => {
      window.removeEventListener(APP_UPDATE_AVAILABLE_EVENT, handleAvailableUpdate);
      window.removeEventListener("focus", syncPendingUpdate);
      window.removeEventListener("pageshow", syncPendingUpdate);
      document.removeEventListener("visibilitychange", syncPendingUpdate);
    };
  }, []);

  const postponeUpdate = () => {
    if (!availableUpdate) return;
    snoozeAppUpdate(availableUpdate.buildId);
    setAvailableUpdate(null);
  };

  const refreshNow = async () => {
    if (!availableUpdate || isRefreshing) return;
    setIsRefreshing(true);
    await clearCacheAndHardRefresh(availableUpdate.buildId);
  };

  return (
    <AlertDialog
      open={Boolean(availableUpdate)}
      onOpenChange={(open) => {
        if (!open && !isRefreshing) postponeUpdate();
      }}
    >
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>A new update is available</AlertDialogTitle>
          <AlertDialogDescription>
            Refresh to load the latest version. SpareLynk will clear its cached app files and perform a hard refresh. If the update still does not appear, press Ctrl+F5 for a manual hard refresh.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel type="button" disabled={isRefreshing}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            type="button"
            className="gap-2"
            disabled={isRefreshing}
            onClick={(event) => {
              event.preventDefault();
              void refreshNow();
            }}
          >
            <RefreshCw className={isRefreshing ? "size-4 animate-spin" : "size-4"} />
            {isRefreshing ? "Hard refreshing…" : "Hard refresh"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
